import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  CONTEST_RESOURCE_CATEGORIES,
  contestCandidateName,
  contestProjectTitle,
  type ContestRecipient,
  type ContestResourceDocument,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { ContestMaturationPanel, ensureMaturationSteps } from '@/features/concours/ContestMaturationPanel';
import { ContestDocumentsPanel } from '@/features/concours/ContestDocumentsPanel';
import { ContestDossierPanel } from '@/features/concours/ContestDossierPanel';
import {
  deleteContestResourceDocument,
  signContestResourceDocumentUrl,
  uploadContestResourceDocument,
} from '@/lib/bunnyStorage';
import { downloadTextFile } from '@/lib/downloadText';
import { useToast } from '@/features/ui/toast/useToast';

const CATEGORY_LABELS = Object.fromEntries(CONTEST_RESOURCE_CATEGORIES.map((c) => [c.key, c.label]));

type ContestRow = {
  id: string;
  name: string;
  is_active: boolean;
  code: string;
  challenge_year: number | null;
  contest_type?: string;
  registration_opens_at?: string | null;
  registration_closes_at?: string | null;
  status?: string;
};

type RegRow = {
  id: string;
  form_data: Record<string, string>;
  contest_id: string;
  user_id: string;
  created_at: string;
};

type DetailTab = 'dossier' | 'documents' | 'maturation';
type ContestTypeKey = 'general' | 'special' | 'ambassadors';

type ContestEvaluationRow = {
  registration_id: string;
  current_level: string;
  jury_decision: string;
  jury_observations: string;
};

const CONTEST_TYPE_OPTIONS: { value: ContestTypeKey; label: string }[] = [
  { value: 'general', label: 'Concours Excellence général' },
  { value: 'special', label: 'Concours Excellence spécial' },
  { value: 'ambassadors', label: 'Concours des ambassadeurs' },
];

const CONTEST_STATUS_OPTIONS = [
  { value: 'launched', label: 'Lancé' },
  { value: 'suspended', label: 'Suspendu' },
  { value: 'closed', label: 'Clôturé' },
] as const;

const emptyRecipient = {
  contest_id: '',
  project_name: '',
  bearer_name: '',
  entrepreneurial_solution: '',
  country_code: '',
  country_name: '',
  sort_order: 0,
};

export function AdminConcoursPage() {
  const { profile, isAdmin } = useAuth();
  const toast = useToast();
  const [contests, setContests] = useState<ContestRow[]>([]);
  const [regs, setRegs] = useState<RegRow[]>([]);
  const [resources, setResources] = useState<ContestResourceDocument[]>([]);
  const [recipients, setRecipients] = useState<ContestRecipient[]>([]);
  const [selectedReg, setSelectedReg] = useState<RegRow | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('dossier');
  const [newCode, setNewCode] = useState('');
  const [newType, setNewType] = useState<ContestTypeKey>('general');
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newOpenAt, setNewOpenAt] = useState('');
  const [newCloseAt, setNewCloseAt] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('reglement');
  const [externalUrl, setExternalUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [recipientForm, setRecipientForm] = useState(emptyRecipient);
  const [editingRecipientId, setEditingRecipientId] = useState<string | null>(null);
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [evaluationByRegId, setEvaluationByRegId] = useState<Record<string, ContestEvaluationRow>>({});
  const [evaluationForm, setEvaluationForm] = useState({
    current_level: '',
    jury_decision: '',
    jury_observations: '',
  });
  const [regYearFilter, setRegYearFilter] = useState('all');
  const [regTypeFilter, setRegTypeFilter] = useState<'all' | ContestTypeKey>('all');

  const contestNameById = Object.fromEntries(contests.map((c) => [c.id, c.name]));
  const challenges = contests.filter((c) => c.challenge_year != null);
  const contestById = Object.fromEntries(contests.map((c) => [c.id, c]));
  const availableYears = [...new Set(contests.map((c) => c.challenge_year).filter((x): x is number => x != null))].sort(
    (a, b) => b - a,
  );

  async function load() {
    const [c, r, docs, rec, evals] = await Promise.all([
      supabase
        .from('contests')
        .select('id, name, is_active, code, challenge_year, contest_type, registration_opens_at, registration_closes_at, status')
        .order('challenge_year', { ascending: false, nullsFirst: false })
        .order('code'),
      supabase
        .from('contest_registrations')
        .select('id, form_data, contest_id, user_id, created_at')
        .order('created_at', { ascending: false }),
      supabase
        .from('contest_resource_documents')
        .select(
          'id, title, description, category, contest_id, file_name, storage_path, external_url, is_published, sort_order, created_at',
        )
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false }),
      supabase
        .from('contest_recipients')
        .select(
          'id, contest_id, project_name, bearer_name, entrepreneurial_solution, country_code, country_name, sort_order',
        )
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('contest_evaluations')
        .select('registration_id, current_level, jury_decision, jury_observations'),
    ]);
    if (c.error) toast.error(c.error.message);
    if (r.error) toast.error(r.error.message);
    if (docs.error) toast.error(docs.error.message);
    if (rec.error) toast.error(rec.error.message);
    if (evals.error) toast.error(evals.error.message);
    const nextContests = (c.data ?? []) as ContestRow[];
    setContests(nextContests);
    const nextRegs = (r.data ?? []) as RegRow[];
    setRegs(nextRegs);
    setResources((docs.data ?? []) as ContestResourceDocument[]);
    setRecipients((rec.data ?? []) as ContestRecipient[]);
    setEvaluationByRegId(
      Object.fromEntries(
        ((evals.data ?? []) as ContestEvaluationRow[]).map((row) => [row.registration_id, row]),
      ),
    );
    setSelectedReg((prev) => (prev ? nextRegs.find((x) => x.id === prev.id) ?? null : null));
    setRecipientForm((prev) => {
      if (prev.contest_id) return prev;
      const preferred =
        nextContests.find((x) => x.status === 'launched' && x.challenge_year != null) ??
        nextContests.find((x) => x.challenge_year != null);
      return preferred ? { ...prev, contest_id: preferred.id } : prev;
    });
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const evaluation = selectedReg ? evaluationByRegId[selectedReg.id] : null;
    setEvaluationForm({
      current_level: evaluation?.current_level ?? '',
      jury_decision: evaluation?.jury_decision ?? '',
      jury_observations: evaluation?.jury_observations ?? '',
    });
  }, [selectedReg, evaluationByRegId]);

  function contestTypeLabel(value?: string) {
    return CONTEST_TYPE_OPTIONS.find((item) => item.value === value)?.label ?? value ?? 'Concours';
  }

  async function updateContestStatus(id: string, status: 'launched' | 'suspended' | 'closed') {
    if (!isAdmin) return;
    await supabase
      .from('contests')
      .update({
        status,
        is_active: status === 'launched',
        activated_at: status === 'launched' ? new Date().toISOString() : null,
        deactivated_at: status !== 'launched' ? new Date().toISOString() : null,
      })
      .eq('id', id);
    await load();
  }

  async function createContest(e: FormEvent) {
    e.preventDefault();
    if (!isAdmin) return;
    const code = newCode.trim().toLowerCase().replace(/\s+/g, '_');
    const year = Number(newYear);
    const contestName = contestTypeLabel(newType);
    const { error: err } = await supabase.from('contests').insert({
      code,
      name: contestName,
      contest_type: newType,
      challenge_year: Number.isFinite(year) ? year : null,
      registration_opens_at: newOpenAt ? new Date(newOpenAt).toISOString() : null,
      registration_closes_at: newCloseAt ? new Date(newCloseAt).toISOString() : null,
      is_active: false,
      status: 'draft',
    });
    if (err) toast.error(err.message);
    else {
      toast.success('Édition créée.');
      setNewCode('');
      setNewType('general');
      setNewYear(String(new Date().getFullYear()));
      setNewOpenAt('');
      setNewCloseAt('');
      await load();
    }
  }

  async function updateContestField(id: string, payload: Partial<ContestRow>) {
    if (!isAdmin) return;
    const { error: err } = await supabase.from('contests').update(payload).eq('id', id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function updateChallengeYear(id: string, yearRaw: string) {
    if (!isAdmin) return;
    const year = Number(yearRaw);
    await updateContestField(id, { challenge_year: Number.isFinite(year) && year > 0 ? year : null });
  }

  function resetRecipientForm() {
    setEditingRecipientId(null);
    setRecipientForm((prev) => ({
      ...emptyRecipient,
      contest_id: prev.contest_id || challenges[0]?.id || '',
    }));
  }

  function startEditRecipient(row: ContestRecipient) {
    setEditingRecipientId(row.id);
    setRecipientForm({
      contest_id: row.contest_id,
      project_name: row.project_name,
      bearer_name: row.bearer_name,
      entrepreneurial_solution: row.entrepreneurial_solution ?? '',
      country_code: row.country_code ?? '',
      country_name: row.country_name ?? '',
      sort_order: row.sort_order ?? 0,
    });
  }

  async function saveRecipient(e: FormEvent) {
    e.preventDefault();
    if (!isAdmin) return;
    setSavingRecipient(true);
    try {
      const payload = {
        contest_id: recipientForm.contest_id,
        project_name: recipientForm.project_name.trim(),
        bearer_name: recipientForm.bearer_name.trim(),
        entrepreneurial_solution: recipientForm.entrepreneurial_solution.trim(),
        country_code: recipientForm.country_code.trim().toUpperCase(),
        country_name: recipientForm.country_name.trim(),
        sort_order: Number(recipientForm.sort_order) || 0,
      };
      if (!payload.contest_id) {
        toast.error('Sélectionnez un challenge.');
        return;
      }
      if (editingRecipientId) {
        const { error: err } = await supabase
          .from('contest_recipients')
          .update(payload)
          .eq('id', editingRecipientId);
        if (err) throw err;
        toast.success('Récipiendaire mis à jour.');
      } else {
        const { error: err } = await supabase.from('contest_recipients').insert(payload);
        if (err) throw err;
        toast.success('Récipiendaire ajouté.');
      }
      resetRecipientForm();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    } finally {
      setSavingRecipient(false);
    }
  }

  async function removeRecipient(row: ContestRecipient) {
    if (!isAdmin) return;
    if (!window.confirm(`Supprimer « ${row.project_name} » ?`)) return;
    const { error: err } = await supabase.from('contest_recipients').delete().eq('id', row.id);
    if (err) toast.error(err.message);
    else {
      toast.success('Récipiendaire supprimé.');
      if (editingRecipientId === row.id) resetRecipientForm();
      await load();
    }
  }

  async function openDossier(reg: RegRow, nextTab: DetailTab = 'dossier') {
    await ensureMaturationSteps(reg.id);
    setSelectedReg(reg);
    setDetailTab(nextTab);
    requestAnimationFrame(() => {
      document.getElementById('admin-contest-dossier')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  const filteredRegs = useMemo(() => {
    return regs.filter((row) => {
      const contest = contestById[row.contest_id];
      if (!contest) return false;
      if (regYearFilter !== 'all' && String(contest.challenge_year ?? '') !== regYearFilter) return false;
      if (regTypeFilter !== 'all' && contest.contest_type !== regTypeFilter) return false;
      return true;
    });
  }, [contestById, regTypeFilter, regYearFilter, regs]);

  function exportRegistrations() {
    if (filteredRegs.length === 0) return;
    const lines = [
      ['Année', 'Type concours', 'Concours', 'Candidat', 'Projet', 'Date dépôt', 'Décision jury'].join(';'),
      ...filteredRegs.map((row) => {
        const contest = contestById[row.contest_id];
        const evaluation = evaluationByRegId[row.id];
        return [
          contest?.challenge_year ?? '',
          contestTypeLabel(contest?.contest_type),
          contest?.name ?? '',
          contestCandidateName(row.form_data) || '',
          contestProjectTitle(row.form_data) || '',
          new Date(row.created_at).toLocaleDateString('fr-FR'),
          evaluation?.jury_decision ?? '',
        ].join(';');
      }),
    ];
    downloadTextFile('liste-candidats-concours.csv', lines.join('\n'));
  }

  async function saveEvaluation() {
    if (!isAdmin || !selectedReg) return;
    const { error: err } = await supabase.from('contest_evaluations').upsert(
      {
        registration_id: selectedReg.id,
        current_level: evaluationForm.current_level.trim(),
        jury_decision: evaluationForm.jury_decision.trim(),
        jury_observations: evaluationForm.jury_observations.trim(),
        updated_by: profile?.id ?? null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'registration_id' },
    );
    if (err) {
      toast.error(err.message);
      return;
    }
    toast.success('Évaluation du candidat enregistrée.');
    await load();
  }

  async function publishResource(e: FormEvent) {
    e.preventDefault();
    if (!isAdmin || !profile) return;
    setPublishing(true);
    try {
      const url = externalUrl.trim();
      let storagePath: string | null = null;
      let fileName: string | null = null;

      if (file) {
        const safeName = file.name.replace(/[^\w.\-]+/g, '_');
        storagePath = `resources/${Date.now()}_${safeName}`;
        await uploadContestResourceDocument({ storagePath, file });
        fileName = file.name;
      }

      if (!storagePath && !url) {
        toast.error('Ajoutez un fichier ou une URL externe.');
        return;
      }

      const { error: err } = await supabase.from('contest_resource_documents').insert({
        title: title.trim(),
        description: description.trim(),
        category,
        file_name: fileName,
        storage_path: storagePath,
        external_url: url || null,
        is_published: true,
        created_by: profile.id,
      });
      if (err) throw err;

      toast.success('Document ajouté à la base documentaire.');
      setTitle('');
      setDescription('');
      setCategory('reglement');
      setExternalUrl('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Publication impossible');
    } finally {
      setPublishing(false);
    }
  }

  async function togglePublished(doc: ContestResourceDocument) {
    if (!isAdmin) return;
    const { error: err } = await supabase
      .from('contest_resource_documents')
      .update({ is_published: !doc.is_published, updated_at: new Date().toISOString() })
      .eq('id', doc.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function removeResource(doc: ContestResourceDocument) {
    if (!isAdmin) return;
    if (!window.confirm(`Supprimer « ${doc.title} » de la base documentaire ?`)) return;
    const { error: err } = await supabase.from('contest_resource_documents').delete().eq('id', doc.id);
    if (err) {
      toast.error(err.message);
      return;
    }
    if (doc.storage_path) {
      try {
        await deleteContestResourceDocument({ storagePath: doc.storage_path });
      } catch {
        // Fichier éventuellement déjà absent
      }
    }
    toast.success('Document retiré.');
    await load();
  }

  async function openResource(doc: ContestResourceDocument) {
    try {
      if (doc.external_url) {
        window.open(doc.external_url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!doc.storage_path) return;
      const url = await signContestResourceDocumentUrl({ storagePath: doc.storage_path });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ouverture impossible');
    }
  }

  return (
    <div>
      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Récipiendaires</h2>
        <p className="muted">
          Liste affichée aux abonnés sur Concours Excellence, filtrable par année / challenge.
        </p>
        {isAdmin ? (
          <form onSubmit={(e) => void saveRecipient(e)} style={{ marginBottom: '1.25rem' }}>
            <div className="grid-2">
              <div className="field">
                <label>Challenge</label>
                <select
                  required
                  value={recipientForm.contest_id}
                  onChange={(e) => setRecipientForm({ ...recipientForm, contest_id: e.target.value })}
                >
                  <option value="">Sélectionner…</option>
                  {challenges.map((c) => (
                    <option key={c.id} value={c.id}>
                      Challenge {c.challenge_year} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Ordre</label>
                <input
                  type="number"
                  value={recipientForm.sort_order}
                  onChange={(e) =>
                    setRecipientForm({ ...recipientForm, sort_order: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="field">
                <label>Projet</label>
                <input
                  required
                  value={recipientForm.project_name}
                  onChange={(e) => setRecipientForm({ ...recipientForm, project_name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Porteur</label>
                <input
                  required
                  value={recipientForm.bearer_name}
                  onChange={(e) => setRecipientForm({ ...recipientForm, bearer_name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Code pays (ex. CIV)</label>
                <input
                  value={recipientForm.country_code}
                  onChange={(e) => setRecipientForm({ ...recipientForm, country_code: e.target.value })}
                  maxLength={3}
                />
              </div>
              <div className="field">
                <label>Pays</label>
                <input
                  value={recipientForm.country_name}
                  onChange={(e) => setRecipientForm({ ...recipientForm, country_name: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label>Solution entrepreneuriale</label>
              <textarea
                rows={2}
                value={recipientForm.entrepreneurial_solution}
                onChange={(e) =>
                  setRecipientForm({ ...recipientForm, entrepreneurial_solution: e.target.value })
                }
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={savingRecipient}>
                {editingRecipientId ? 'Enregistrer' : 'Ajouter le récipiendaire'}
              </button>
              {editingRecipientId ? (
                <button type="button" className="btn btn-ghost" onClick={resetRecipientForm}>
                  Annuler
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
        {recipients.length === 0 ? (
          <p className="muted">Aucun récipiendaire.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {recipients.map((row) => (
              <li
                key={row.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <div>
                  <strong>{row.project_name}</strong>
                  <div className="muted">
                    {row.bearer_name}
                    {' · '}
                    {contestNameById[row.contest_id] ?? 'Challenge'}
                    {row.country_code ? ` · ${row.country_code}` : ''}
                  </div>
                </div>
                {isAdmin ? (
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => startEditRecipient(row)}>
                      Modifier
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => void removeRecipient(row)}>
                      Supprimer
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Documentation concours</h2>
        <p className="muted">Base de documents consultable par les abonnés (plusieurs fichiers).</p>
        {isAdmin ? (
          <form onSubmit={(e) => void publishResource(e)} style={{ marginBottom: '1.25rem' }}>
            <div className="grid-2">
              <div className="field">
                <label>Titre</label>
                <input required value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="field">
                <label>Catégorie</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CONTEST_RESOURCE_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Fichier (PDF, Word…)</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="field">
                <label>Ou URL externe</label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={publishing}>
              {publishing ? 'Publication…' : 'Publier dans la base'}
            </button>
          </form>
        ) : null}
        {resources.length === 0 ? (
          <p className="muted">Aucun document dans la base.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {resources.map((doc) => (
              <li
                key={doc.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <div>
                  <strong>{doc.title}</strong>
                  <div className="muted">
                    {CATEGORY_LABELS[doc.category] ?? doc.category}
                    {' · '}
                    {doc.is_published ? 'publié' : 'masqué'}
                    {doc.file_name ? ` · ${doc.file_name}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => void openResource(doc)}>
                    Voir
                  </button>
                  {isAdmin ? (
                    <>
                      <button type="button" className="btn btn-ghost" onClick={() => void togglePublished(doc)}>
                        {doc.is_published ? 'Masquer' : 'Publier'}
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => void removeResource(doc)}>
                        Supprimer
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Éditions concours</h2>
        {isAdmin ? (
          <form onSubmit={createContest} style={{ marginBottom: '1rem' }}>
            <div className="grid-2">
              <div className="field">
                <label>Code</label>
                <input
                  required
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder="ex: challenge_2028"
                />
              </div>
              <div className="field">
                <label>Type de concours</label>
                <select value={newType} onChange={(e) => setNewType(e.target.value as ContestTypeKey)}>
                  {CONTEST_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Année du challenge</label>
                <input
                  required
                  type="number"
                  min={2000}
                  max={2100}
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Date d'ouverture</label>
                <input type="datetime-local" value={newOpenAt} onChange={(e) => setNewOpenAt(e.target.value)} />
              </div>
              <div className="field">
                <label>Date de clôture</label>
                <input type="datetime-local" value={newCloseAt} onChange={(e) => setNewCloseAt(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit">
              Créer une édition
            </button>
          </form>
        ) : null}
        <ul>
          {contests.map((c) => (
            <li key={c.id} style={{ marginBottom: '0.65rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>
                  {contestTypeLabel(c.contest_type)} ({c.code}) — {c.status ?? (c.is_active ? 'launched' : 'draft')}
                </span>
                {isAdmin ? (
                  <>
                    <label className="muted" style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                      Année
                      <input
                        type="number"
                        min={2000}
                        max={2100}
                        style={{ width: '5.5rem' }}
                        defaultValue={c.challenge_year ?? ''}
                        onBlur={(e) => {
                          const next = e.target.value;
                          const prev = c.challenge_year != null ? String(c.challenge_year) : '';
                          if (next !== prev) void updateChallengeYear(c.id, next);
                        }}
                      />
                    </label>
                    <label className="muted" style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                      Ouverture
                      <input
                        type="datetime-local"
                        defaultValue={c.registration_opens_at ? c.registration_opens_at.slice(0, 16) : ''}
                        onBlur={(e) =>
                          void updateContestField(c.id, {
                            registration_opens_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                          })
                        }
                      />
                    </label>
                    <label className="muted" style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                      Clôture
                      <input
                        type="datetime-local"
                        defaultValue={c.registration_closes_at ? c.registration_closes_at.slice(0, 16) : ''}
                        onBlur={(e) =>
                          void updateContestField(c.id, {
                            registration_closes_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                          })
                        }
                      />
                    </label>
                    {CONTEST_STATUS_OPTIONS.map((status) => (
                      <button
                        key={status.value}
                        type="button"
                        className={`btn ${c.status === status.value ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => void updateContestStatus(c.id, status.value)}
                      >
                        {status.label}
                      </button>
                    ))}
                  </>
                ) : c.challenge_year ? (
                  <span className="muted">· {c.challenge_year}</span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'end' }}>
          <h3 style={{ margin: 0 }}>Gestion des concours</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div className="field" style={{ minWidth: '12rem', marginBottom: 0 }}>
              <label>Année</label>
              <select value={regYearFilter} onChange={(e) => setRegYearFilter(e.target.value)}>
                <option value="all">Toutes</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ minWidth: '16rem', marginBottom: 0 }}>
              <label>Type de concours</label>
              <select value={regTypeFilter} onChange={(e) => setRegTypeFilter(e.target.value as 'all' | ContestTypeKey)}>
                <option value="all">Tous</option>
                {CONTEST_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="btn btn-ghost" onClick={exportRegistrations} disabled={filteredRegs.length === 0}>
              Télécharger la liste
            </button>
          </div>
        </div>
        <p className="muted">Filtrez par année et type de concours pour gérer les candidatures.</p>
        {filteredRegs.length === 0 ? <p className="muted">Aucune inscription pour ce filtre.</p> : null}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {filteredRegs.map((r) => {
            const active = selectedReg?.id === r.id;
            const evaluation = evaluationByRegId[r.id];
            const contest = contestById[r.contest_id];
            return (
              <li
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid var(--line)',
                  background: active ? 'rgba(74, 126, 176, 0.08)' : undefined,
                }}
              >
                <div>
                  <strong>{contestProjectTitle(r.form_data) || r.id}</strong>
                  <div className="muted">{contestCandidateName(r.form_data) || '—'}</div>
                  <div className="muted" style={{ fontSize: '0.85rem' }}>
                    {contestNameById[r.contest_id] ?? 'Concours'}
                    {' · '}
                    {new Date(r.created_at).toLocaleDateString('fr-FR')}
                    {contest?.challenge_year ? ` · ${contest.challenge_year}` : ''}
                  </div>
                  {evaluation?.jury_decision ? (
                    <div className="muted" style={{ fontSize: '0.85rem' }}>
                      Jury : {evaluation.jury_decision}
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => void openDossier(r)}>
                    Ouvrir
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void openDossier(r, 'maturation')}
                  >
                    Évaluer
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {selectedReg ? (
        <section className="panel" id="admin-contest-dossier">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h3 style={{ marginTop: 0 }}>Dossier candidature</h3>
            <button type="button" className="btn btn-ghost" onClick={() => setSelectedReg(null)}>
              Fermer
            </button>
          </div>

          <div className="u-steps" role="tablist" aria-label="Sections du dossier" style={{ marginBottom: '1rem' }}>
            {(
              [
                { id: 'dossier' as const, label: 'Formulaire' },
                { id: 'documents' as const, label: 'Documents' },
                { id: 'maturation' as const, label: 'Maturation' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={detailTab === tab.id}
                className={`u-step ${detailTab === tab.id ? 'is-active' : ''}`}
                onClick={() => setDetailTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {detailTab === 'dossier' ? (
            <ContestDossierPanel
              formData={selectedReg.form_data}
              meta={{
                contestName: contestNameById[selectedReg.contest_id],
                createdAt: selectedReg.created_at,
                userId: selectedReg.user_id,
                registrationId: selectedReg.id,
              }}
            />
          ) : null}

          {detailTab === 'documents' ? (
            <ContestDocumentsPanel registrationId={selectedReg.id} readOnly />
          ) : null}

          {detailTab === 'maturation' ? (
            <>
              <ContestMaturationPanel registrationId={selectedReg.id} />
              <section className="panel" style={{ marginTop: '1rem' }}>
                <h4 style={{ marginTop: 0 }}>Évaluation du jury</h4>
                <div className="grid-2">
                  <div className="field">
                    <label>Niveau actuel</label>
                    <input
                      value={evaluationForm.current_level}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, current_level: e.target.value })}
                      placeholder="Ex. Présélection, finale, terrain..."
                    />
                  </div>
                  <div className="field">
                    <label>Décision jury</label>
                    <select
                      value={evaluationForm.jury_decision}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, jury_decision: e.target.value })}
                    >
                      <option value="">Sélectionner…</option>
                      <option value="recalé">Recalé</option>
                      <option value="admis(e)">Admis(e)</option>
                      <option value="ajourné">Ajourné</option>
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label>Observations du jury</label>
                  <textarea
                    rows={4}
                    value={evaluationForm.jury_observations}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, jury_observations: e.target.value })}
                  />
                </div>
                <button type="button" className="btn btn-primary" onClick={() => void saveEvaluation()}>
                  Enregistrer l’évaluation
                </button>
              </section>
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
